import {
  Stack,
  Tooltip,
  Typography,
  Avatar,
  AvatarGroup,
  alpha,
  SvgIcon,
  Card,
  CardContent,
  ButtonBase,
  IconButton,
  Box,
  debounce,
  Skeleton,
  TooltipProps,
  tooltipClasses,
  Link,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnGroupingModel,
  GridColumnHeaders,
  GridPaginationModel,
  GridRenderCellParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridLogicOperator,
  GridToolbar,
  GridSortModel,
  GridColumnsPanel,
} from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useRef, useState, useCallback, type JSXElementConstructor } from 'react';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings/selectors';
import Iconify from 'src/components/iconify';
import { FilterPanelCustom } from 'src/components/data-grid/filter-panel-custom';
import { styled } from '@mui/material/styles';
import { useLocales } from 'src/locales';
import { useExportMutation } from 'src/context/api/exports/api';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from 'src/context/store';
import { useBuildColumns, useSearchTermsGridData } from './hook';
import { useNonBrandFilterArgs } from '../use-non-brand-filter-args';
import { registerPageRows } from './inline-evolution';
import { isLeadWorkspace } from '../workspace-type';
import {
  COLUMN_HEADER_H,
  FOOTER_H,
  GROUP_HEADER_H,
  NB_GRID_HEIGHT,
  ROW_H,
  TOOLBAR_H,
  fitsWithoutCap as rowsFitWithoutCap,
} from './grid-height';
import { COLUMN_GROUPS, columnsForTemplate, defaultVisibleColumns } from './search-terms/columns/visibility';
import NbColumnsManagement, { type NbColumnsManagementProps } from './columns-management';
import NbMetricEvolutionModal from '../metric-evolution/nb-metric-evolution-modal';

const COMPARISON_SUFFIXES = ['_compare', '_diff'] as const;

const getBaseField = (field: string) =>
  COMPARISON_SUFFIXES.reduce((baseField, suffix) => baseField.replace(suffix, ''), field);

const isComparisonField = (field: string) =>
  COMPARISON_SUFFIXES.some((suffix) => field.endsWith(suffix));

const normalizeColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
  const normalized = { ...model };

  Object.keys(normalized).forEach((field) => {
    if (!isComparisonField(field)) {
      return;
    }

    const baseField = getBaseField(field);
    if (normalized[baseField] === false) {
      normalized[field] = false;
    }
  });

  return normalized;
};

const TOOLBAR_ICON_SX = {
  width: 26,
  height: 26,
  minWidth: 26,
  padding: '4px',
  borderRadius: '6px',
  color: '#919EAB',
  '&:hover': { backgroundColor: 'rgba(145, 158, 171, 0.08)' },
};

const ANALYSIS_CHART_BUTTON_SX = {
  width: 26,
  height: 26,
  minWidth: 26,
  padding: '4px',
  borderRadius: '6px',
  backgroundColor: 'rgba(145, 158, 171, 0.20)',
  '&:hover': { backgroundColor: 'rgba(145, 158, 171, 0.40)' },
};

/** Icon of the Conversions table's "Show analysis chart" button. */
const AnalysisChartIcon = () => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" sx={{ display: 'block' }}>
    <path
      d="M15 15V9.75M9 15V7.5M3 15L3 12M10.055 3.77063L13.9313 5.22425M8.09908 4.05069L3.90017 7.19987M15.7955 4.8295C16.2348 5.26884 16.2348 5.98116 15.7955 6.4205C15.3562 6.85984 14.6438 6.85984 14.2045 6.4205C13.7652 5.98116 13.7652 5.26884 14.2045 4.8295C14.6438 4.39016 15.3562 4.39016 15.7955 4.8295ZM3.7955 7.0795C4.23483 7.51884 4.23483 8.23116 3.7955 8.6705C3.35616 9.10984 2.64384 9.10984 2.2045 8.6705C1.76517 8.23116 1.76517 7.51884 2.2045 7.0795C2.64384 6.64016 3.35616 6.64016 3.7955 7.0795ZM9.7955 2.5795C10.2348 3.01884 10.2348 3.73116 9.7955 4.1705C9.35616 4.60983 8.64384 4.60983 8.2045 4.1705C7.76517 3.73116 7.76517 3.01884 8.2045 2.5795C8.64384 2.14017 9.35616 2.14017 9.7955 2.5795Z"
      stroke="#5E6D7B"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

/** Header class per column group (Performance keeps the former Conversion style). */
const GROUP_HEADER_CLASSES: Record<string, string> = {
  Visibility: 'visibility-group-header',
  Traffic: 'traffic-group-header',
  Performance: 'conversion-group-header',
};

type GridPanel = 'columns' | 'filters';

type NbGridToolbarProps = {
  onOpenMetricEvolution: () => void;
  showEvolutionSwitch: boolean;
  evolutionMode: boolean;
  onToggleEvolution: () => void;
  /** Mousedown on a panel button, before the panel's click-away closes it. */
  onPanelButtonDown: (panel: GridPanel) => void;
  /** Opens the panel right below its button (or closes it, when it was open). */
  onTogglePanel: (panel: GridPanel, button: HTMLElement) => void;
  onExport: () => void;
  exportDisabled: boolean;
};

/**
 * Same header bar as the Traffic and Conversions tables: 40px, actions right-aligned.
 * Declared at module level: a component created during render would be remounted by DataGridPro on
 * every render, closing its tooltips and dropping the keyboard focus.
 */
function NbGridToolbar({
  onOpenMetricEvolution,
  showEvolutionSwitch,
  evolutionMode,
  onToggleEvolution,
  onPanelButtonDown,
  onTogglePanel,
  onExport,
  exportDisabled,
}: NbGridToolbarProps) {
  return (
    <Stack
      data-testid="nb-grid-toolbar"
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={1.5}
      px={2.5}
      sx={{ height: TOOLBAR_H, borderBottom: '1px solid rgba(145, 158, 171, 0.12)' }}
    >
      {/* Same button as the Conversions table */}
      <Tooltip title="Show analysis chart" placement="top">
        <IconButton aria-label="Show analysis chart" size="small" onClick={onOpenMetricEvolution} sx={ANALYSIS_CHART_BUTTON_SX}>
          <AnalysisChartIcon />
        </IconButton>
      </Tooltip>
      {showEvolutionSwitch && (
        <Tooltip
          describeChild
          title={
            evolutionMode
              ? 'Showing % evolution — click for absolute difference'
              : 'Showing absolute difference — click for % evolution'
          }
          placement="top"
        >
          {/* Stays blue in both states; only the icon tells % evolution from absolute difference */}
          <ButtonBase
            onClick={onToggleEvolution}
            aria-pressed={evolutionMode}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              px: '6px',
              py: '4px',
              borderRadius: '6px',
              backgroundColor: 'rgba(145, 158, 171, 0.08)',
              color: '#1E88E5',
            }}
          >
            <Iconify icon={evolutionMode ? 'material-symbols:percent-rounded' : 'material-symbols:tag-rounded'} width={16} />
            <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: '16.8px', color: 'inherit', whiteSpace: 'nowrap', mx: '2px' }}>
              evolution
            </Typography>
            <Iconify icon="mi:switch" width={16} />
          </ButtonBase>
        </Tooltip>
      )}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Tooltip title="Show/hide columns" placement="top">
          <IconButton
            aria-label="Show/hide columns"
            size="small"
            onMouseDown={() => onPanelButtonDown('columns')}
            onClick={(event) => onTogglePanel('columns', event.currentTarget)}
            sx={TOOLBAR_ICON_SX}
          >
            <Iconify icon="mingcute:calendar-week-line" width={18} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Filters" placement="top">
          <IconButton
            aria-label="Filters"
            size="small"
            onMouseDown={() => onPanelButtonDown('filters')}
            onClick={(event) => onTogglePanel('filters', event.currentTarget)}
            sx={TOOLBAR_ICON_SX}
          >
            <Iconify icon="ic:round-filter-list" width={18} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export" placement="top">
          {/* span: a disabled button does not fire the tooltip events */}
          <span>
            <IconButton aria-label="Export" size="small" disabled={exportDisabled} onClick={onExport} sx={TOOLBAR_ICON_SX}>
              <Iconify icon="tabler:download" width={18} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

export default function SearchTermsGrid() {
  const { t } = useLocales();
  const workspace = useCurrentWorkspace();
  const [exportMutation] = useExportMutation();
  const isLead = isLeadWorkspace(workspace?.template);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'search_term', sort: 'desc' },
  ]);
  // New rows (header filters, opportunity, column filters, sort) start again from the first page. Reset while
  // rendering (not in an effect) so that the previous, possibly out-of-range page is never requested.
  const filterArgs = useNonBrandFilterArgs();
  const opportunity = useSelector((state: RootState) => state.filters.nonBrandOpportunity);
  const rowsKey = JSON.stringify({ filterArgs, opportunity, filters: filterModel.items, sortModel });
  const [paginatedRowsKey, setPaginatedRowsKey] = useState(rowsKey);
  if (paginatedRowsKey !== rowsKey) {
    setPaginatedRowsKey(rowsKey);
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
  }
  const { searchTermsGrid, isLoading, isError, isFetching, job_id } = useSearchTermsGridData(
    Object.assign(
      {},
      { ...filterModel, items: filterModel.items.filter((item) => ![null, undefined, ''].includes(item.value)) },
      paginationModel
    ),
    sortModel
  );
  const compare = useSelector((state: RootState) => state.filters.compare);
  // % evolution (default) vs. absolute difference, for the inline evolution of each cell.
  const [evolutionMode, setEvolutionMode] = useState(true);
  const [metricEvolutionOpen, setMetricEvolutionOpen] = useState(false);

  const apiRef = useRef<any>(null);
  // Filter by workspace type only after building: useBuildColumns calls hooks per column.
  const columns = columnsForTemplate(useBuildColumns('search-terms', { evolutionMode }), isLead);
  // Paid CPA (Lead) or Paid ROAS (eCommerce) on top of the maquette's columns
  const defaultVisible = useMemo(() => new Set<string>(defaultVisibleColumns(isLead)), [isLead]);
  // What the columns panel's Reset gives back
  const defaultColumnVisibilityModel = columns.reduce((acc, column) => {
    acc[column.field] = defaultVisible.has(getBaseField(column.field));
    return acc;
  }, {} as GridColumnVisibilityModel);

  const check = (value: any) => {
    if (value === undefined) {
      return false;
    }
    if (value === null) {
      return false;
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    if (typeof value === 'string') {
      return value !== '';
    }
    if (Array.isArray(value)) {
      return value.some(check);
    }
    if (typeof value === 'object') {
      return check(Object.values(value));
    }
    return false;
  }

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    normalizeColumnVisibilityModel(
      columns.reduce((acc, column) => {
        const baseField = getBaseField(column.field);
        const datacol = searchTermsGrid?.data?.map((row: any) => row[baseField]).some(check);
        acc[column.field] = defaultVisible.has(baseField) && datacol;
        return acc;
      }, {} as GridColumnVisibilityModel)
    )
  );

  useEffect(() => {
    setColumnVisibilityModel((currentModel) => {
      const nextModel: GridColumnVisibilityModel = { ...currentModel };
      let hasChanged = false;
      const availableFields = new Set(columns.map((column) => column.field));

      columns.forEach((column) => {
        if (nextModel[column.field] !== undefined) {
          return;
        }

        const baseField = getBaseField(column.field);
        const baseIsVisible = nextModel[baseField] !== false;
        nextModel[column.field] = isComparisonField(column.field)
          ? baseIsVisible
          : defaultVisible.has(baseField);
        hasChanged = true;
      });

      Object.keys(nextModel).forEach((field) => {
        if (availableFields.has(field)) {
          return;
        }
        delete nextModel[field];
        hasChanged = true;
      });

      const normalizedModel = normalizeColumnVisibilityModel(nextModel);
      if (
        !hasChanged &&
        JSON.stringify(normalizedModel) === JSON.stringify(currentModel)
      ) {
        return currentModel;
      }
      return normalizedModel;
    });
  }, [columns, defaultVisible]);

  // MUI's "Show all" deletes the keys of the columns it shows (a missing key means visible): spell them
  // out, otherwise the effect above gives them back their default visibility on the next render.
  const columnFieldsRef = useRef<string[]>([]);
  columnFieldsRef.current = columns.map((column) => column.field);
  const handleColumnVisibilityModelChange = useCallback((nextModel: GridColumnVisibilityModel) => {
    const explicitModel: GridColumnVisibilityModel = { ...nextModel };
    columnFieldsRef.current.forEach((field) => {
      if (explicitModel[field] === undefined) explicitModel[field] = true;
    });
    setColumnVisibilityModel(normalizeColumnVisibilityModel(explicitModel));
  }, []);

  // The columns and filter panels open right below their button (Traffic and Conversions tables pattern).
  const [panelAnchor, setPanelAnchor] = useState<HTMLElement | null>(null);
  const panelWasOpenRef = useRef(false);
  const handlePanelButtonDown = (panel: GridPanel) => {
    const state = apiRef.current?.state?.preferencePanel;
    panelWasOpenRef.current = !!(state?.open && state?.openedPanelValue === panel);
  };
  const handleTogglePanel = (panel: GridPanel, button: HTMLElement) => {
    // Clicking the button of the open panel closes it (its click-away already did, on mouseup)
    if (panelWasOpenRef.current) {
      apiRef.current?.hidePreferences();
      return;
    }
    setPanelAnchor(button);
    if (panel === 'columns') apiRef.current?.showPreferences('columns');
    else apiRef.current?.showFilterPanel();
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const HeaderGroup = (props: any) => {
    return (
      <Stack direction="row" alignItems="center" spacing={0}>
        <Typography
          variant="body1"
          noWrap
          sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
        >
          {props.groupId}
        </Typography>
      </Stack>
    );
  };

  const definedFields = new Set(columns.map((column) => column.field));
  const columnGroupingModel: GridColumnGroupingModel = COLUMN_GROUPS.map(({ groupId, fields }) => ({
    groupId,
    headerName: groupId,
    headerClassName: GROUP_HEADER_CLASSES[groupId],
    headerAlign: 'center',
    // Base, compare and diff columns of the group, limited to the columns actually built.
    children: fields
      .flatMap((field) => [field, ...COMPARISON_SUFFIXES.map((suffix) => field + suffix)])
      .filter((field) => definedFields.has(field))
      .map((field) => ({ field })),
  }));


  const handleExport = () => {
    if (job_id) {
      exportMutation({
        name: `[${workspace?.name}] (Non Branded) Search terms export`,
        queryId: job_id,
        columns: columns
          .map((column) => column.field)
          .filter((column) => {
            return (
              columnVisibilityModel[column as keyof typeof columnVisibilityModel] !==
              false
            );
          }),
        column_tr: columns.reduce(
          (acc: Record<string, string>, column: GridColDef) => {
            acc[column.field] = column.headerName as string;
            return acc;
          },
          {} as Record<string, string>
        ),
        dimensions: ['search_term'],
      })
        .unwrap()
        .then((res) => {
          enqueueSnackbar(
            "Export is running in background, you will be notified when it's done",
            { variant: 'success' }
          );
        })
        .catch((err) => {
          enqueueSnackbar('Error exporting data', { variant: 'error' });
        });
    } else {
      enqueueSnackbar('Unable to export this query', { variant: 'error' });
    }
  };

  const loadedRows = searchTermsGrid?.data;
  const gridRows = useMemo(() => {
    const rows = isLoading || isFetching ? [] : loadedRows || [];
    // In-cell bars are scaled on the max of the loaded rows: registered once per data set.
    registerPageRows(rows);
    return rows;
  }, [isLoading, isFetching, loadedRows]);
  const fitsWithoutCap = rowsFitWithoutCap(gridRows.length);

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(145, 158, 171, 0.12)',
        overflow: 'hidden',
      }}
    >
      <CardContent
        sx={{
          px: 0,
          py: 0,
          overflow: 'visible',
          minHeight: isLoading || isFetching ? 600 : 'auto',
        }}
      >
        {/* Fixed height + internal scroll once the rows no longer fit (ExploreTable pattern). */}
        <Box sx={{ height: fitsWithoutCap ? 'auto' : NB_GRID_HEIGHT }}>
        <DataGridPro
          classes={{
            root: 'search-term-grid',
            columnHeaders: 'search-term-grid-column-headers',
            columnHeader: 'search-term-grid-column-header',
          }}
          apiRef={apiRef}
          columnGroupHeaderHeight={GROUP_HEADER_H}
          pinnedColumns={{ left: ['search_term'], right: ['opportunity'] }}
          loading={isLoading || isFetching}
          rows={gridRows}
          columns={columns}
          rowHeight={ROW_H}
          columnHeaderHeight={COLUMN_HEADER_H}
          disableColumnFilter={true}
          disableColumnMenu={true}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          filterModel={filterModel}
          filterMode="server"
          sortingMode="server"
          columnGroupingModel={columnGroupingModel}
          disableColumnSelector={true}
          disableMultipleColumnsSorting={true}
          onSortModelChange={setSortModel}
          sortModel={sortModel}
          onFilterModelChange={setFilterModel}
          disableDensitySelector
          checkboxSelection={false}
          pagination
          autoHeight={fitsWithoutCap}
          disableRowSelectionOnClick={true}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={searchTermsGrid?.total_rows || 0}
          pageSizeOptions={[10, 20, 50, 100]}
          sx={{
            '& .MuiDataGrid-toolbarContainer': {
              padding: 0,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(145, 158, 171, 0.12)',
              minHeight: FOOTER_H,
            },
          }}
          slots={{
            // Its props come from slotProps.toolbar, not from MUI's GridToolbarProps
            toolbar: NbGridToolbar as JSXElementConstructor<any>,
            filterPanel: FilterPanelCustom,
            // Its defaultModel prop comes from slotProps.columnsManagement
            columnsManagement: NbColumnsManagement as JSXElementConstructor<any>,
            loadingOverlay: (props) => <Skeleton variant="rectangular" height={ROW_H * (paginationModel.pageSize + 2)} />,
          }}
          slotProps={{
            toolbar: ({
              onOpenMetricEvolution: () => setMetricEvolutionOpen(true),
              showEvolutionSwitch: !!compare,
              evolutionMode,
              onToggleEvolution: () => setEvolutionMode((value) => !value),
              onPanelButtonDown: handlePanelButtonDown,
              onTogglePanel: handleTogglePanel,
              onExport: handleExport,
              exportDisabled: isLoading || isFetching,
            } satisfies NbGridToolbarProps) as any,
            ...(panelAnchor ? { panel: { anchorEl: panelAnchor, placement: 'bottom-end' as const } } : {}),
            filterPanel: {
              logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
              getColumnForNewFilter: (args) => (args ? args.columns[0].field : null),
            },
            loadingOverlay: {
              style: {
                backgroundColor: 'unset',

              },
            },
            columnsManagement: {
              getTogglableColumns: (columns: GridColDef[]) =>
                columns.filter((column: any) => column.visibility !== null).map((column: any) => column.field),
              defaultModel: defaultColumnVisibilityModel,
            } satisfies NbColumnsManagementProps as any
          }}
        />
        </Box>
      </CardContent>
      <NbMetricEvolutionModal open={metricEvolutionOpen} onClose={() => setMetricEvolutionOpen(false)} />
    </Card>
  );
}